import { useSessionContext } from "@/contexts/SessionContext";
import { useTaskWatchingContext } from "@/contexts/TaskWatchingContext";
import { useSidebarNavigation } from "@/contexts/SidebarNavigationContext";
import { currentFeatures, oids } from "@/configs/Settings";
import { OpenInNew, Visibility } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  TablePagination,
  Tabs,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import {
  getAppointmentReferenceFromQuestionnaireTask,
  isQuestionnaireTask,
} from "src-next/modules/questionnaire-builder/questionnaire-tasks";
import { PatientQuestionnairesDialog } from "src-next/modules/patient-summary";
import { BundlePatientSummary } from "@/services/fhir";
import { GetConsents } from "@/services/consent";
import { GetTask } from "@/services/fhir/task/GetTask";
import { HTTPResponseStatus } from "@/utils/enum";
import { tablePaginationStyles } from "@/components/GenericTable/styles";
import { useFhirData } from "@/utils/useFhirData";
import { getDependentIdentifier } from "@/utils/patient/getDependentIdentifier";
import {
  dependentPatientReferenceFromRecord,
  fetchDependentPatientSummaryQuestionnaireEntries,
  fetchVerifiedPetQuestionnaireSummaryEntries,
  PATIENT_SUMMARY_ENTRIES_QUESTIONNAIRE_RESPONSE_ONLY,
  patientSummaryIdentifierFromRelatedDependent,
  type ConsentReceivedLite,
} from "@/utils/patient/fetchDependentPatientSummaryQuestionnaireEntries";

const TOUCH_MIN_PX = 44;

/**
 * - `patient` (default): portal humano — dependentes exigem consentimento “received” + `purpose`.
 * - `Tutor`: FluxVet — tutor (CPF) responde pelos pets verificados; sem GetConsents nem gate por purpose.
 */
export type PatientQuestionnaireTasksSectionVariant = "patient" | "tutor";

export interface PatientQuestionnaireTasksSectionProps {
  variant?: PatientQuestionnaireTasksSectionVariant;
}

type TasksTabValue = "open" | "completed" | "all";

const TASKS_TABS: { value: TasksTabValue; label: string }[] = [
  { value: "open", label: "Abertos" },
  { value: "completed", label: "Respondidos" },
  { value: "all", label: "Todos" },
];

const QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL =
  "http://fluxmed.com.br/StructureDefinition/questionnaire-usage-type";

/** Metadados comuns às linhas (titular ou dependente consentido). */
type QuestionnaireSubjectRowMeta = {
  /** Patient/... do sujeito clínico (titular ou dependente). */
  subjectPatientReference: string;
  /** Nome do dependente quando a linha não for do titular. */
  subjectPatientDisplayName?: string;
};

type QuestionnaireListItem =
  | ({
      kind: "task";
      key: string;
      title: string;
      dateIso?: string;
      dateLabel?: string;
      questionnaireReference?: string;
      task: fhir5.Task;
    } & QuestionnaireSubjectRowMeta)
  | ({
      kind: "response";
      key: string;
      title: string;
      dateIso?: string;
      dateLabel?: string;
      questionnaireReference?: string;
      questionnaireResponse: fhir5.QuestionnaireResponse;
      /**
       * Se existir uma Task correspondente, mantemos referência para permitir
       * reaproveitar a mesma lógica de "Ver minhas respostas" quando fizer sentido.
       */
      task?: fhir5.Task;
    } & QuestionnaireSubjectRowMeta);

type EnrichedQuestionnaireResponseRow = {
  bundleEntry: any;
  subjectPatientReference: string;
  subjectPatientDisplayName?: string;
};

function normalizeQuestionnaireReference(ref: unknown): string | undefined {
  const raw = typeof ref === "string" ? ref : "";
  const t = raw.trim();
  return t ? t : undefined;
}

function questionnaireReferenceFromTask(task: fhir5.Task): string | undefined {
  return normalizeQuestionnaireReference(task.focus?.reference);
}

function questionnaireReferenceFromResponse(
  questionnaireResponse: fhir5.QuestionnaireResponse,
): string | undefined {
  const q = questionnaireResponse.questionnaire as unknown;
  if (typeof q === "string") return normalizeQuestionnaireReference(q);
  if (q && typeof q === "object" && "reference" in (q as any)) {
    return normalizeQuestionnaireReference((q as any).reference);
  }
  return undefined;
}

function questionnaireTitleFromResponse(
  questionnaireResponse: fhir5.QuestionnaireResponse,
): string {
  const extensions = questionnaireResponse.extension as unknown;
  if (Array.isArray(extensions)) {
    const ext = extensions.find(
      (e: any) =>
        e?.url ===
        "http://fluxmed.com.br/StructureDefinition/questionnaire-title",
    );
    const t = String((ext as any)?.valueString ?? "").trim();
    if (t) return t;
  }
  const ref = questionnaireReferenceFromResponse(questionnaireResponse);
  return ref ? ref : "Questionário";
}

function dateIsoFromResponse(
  questionnaireResponse: fhir5.QuestionnaireResponse,
): string | undefined {
  const authored = String(
    (questionnaireResponse as any)?.authored ?? "",
  ).trim();
  if (authored) return authored;
  const lastUpdated = String(
    (questionnaireResponse as any)?.meta?.lastUpdated ?? "",
  ).trim();
  return lastUpdated || undefined;
}

function dateLabelFromIso(iso?: string): string | undefined {
  if (!iso) return undefined;
  const d = dayjs(iso);
  if (!d.isValid()) return iso;
  return d.format("DD/MM/YYYY HH:mm");
}

function primaryAuthorReferenceFromResponse(
  questionnaireResponse: fhir5.QuestionnaireResponse,
): string | undefined {
  const a = questionnaireResponse.author as unknown;
  if (!a) return undefined;
  if (Array.isArray(a)) {
    const r = String((a[0] as { reference?: string })?.reference ?? "").trim();
    return r || undefined;
  }
  const r = String((a as { reference?: string })?.reference ?? "").trim();
  return r || undefined;
}

/**
 * Dependente vendo o próprio painel: `subject` sou eu e `author` é outro Patient
 * (responsável que respondeu pelo portal).
 */
function isRespondedByResponsibleForViewer(
  questionnaireResponse: fhir5.QuestionnaireResponse,
  viewerPatientReference: string,
): boolean {
  const subj = String(questionnaireResponse.subject?.reference ?? "").trim();
  const auth = primaryAuthorReferenceFromResponse(questionnaireResponse);
  if (!subj || !auth) return false;
  if (subj !== viewerPatientReference.trim()) return false;
  return auth !== subj;
}

function filterQuestionnaireResponseEntriesWithUsageType(
  bundle: fhir5.Bundle | undefined,
): any[] {
  if (!bundle) return [];
  const questionnaireData = useFhirData.extractPatientSummaryResource(
    bundle,
    "QuestionnaireResponse",
  );
  const questionnaireResponseEntries = questionnaireData?.[0]?.entry ?? [];
  return (
    questionnaireResponseEntries.filter((bundleEntry: any) => {
      const extensions = bundleEntry?.resource?.extension;
      if (!Array.isArray(extensions)) return false;
      return extensions.some(
        (extension: any) =>
          extension?.url === QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
      );
    }) ?? []
  );
}

/**
 * Lista no painel do paciente apenas Tasks de questionário (`code` + `focus`).
 *
 * Estados:
 * - `requested`: aguardando resposta (enviado pelo profissional).
 * - `completed`: Task atualizada após envio do QuestionnaireResponse em
 *   `/questionnaire-respond` (não há marcação manual nesta seção).
 *
 * Botão "Abrir questionário": navega para o fluxo de preenchimento com state
 * necessário para criar o QuestionnaireResponse e concluir a Task.
 *
 * Responsáveis com dependentes verificados + consentimento “received” também veem
 * e respondem questionários dos dependentes (`subject` = dependente; `author` = titular).
 *
 * Modo `Tutor`: mesma UI/ações, porém apenas pets (`patientRelated` verificados), sem consentimento.
 */
export const PatientQuestionnaireTasksSection = memo(
  function PatientQuestionnaireTasksSection({
    variant = "patient",
  }: PatientQuestionnaireTasksSectionProps) {
    const isTutorMode = !currentFeatures.defaultApp;
    const { user, access, patientRelated } = useSessionContext();
    const { allTasks } = useTaskWatchingContext();
    const { setIsSidebarExpanded } = useSidebarNavigation();
    const navigate = useNavigate();

    const [tasksTab, setTasksTab] = useState<TasksTabValue>("open");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [questionnairesDialogOpen, setQuestionnairesDialogOpen] =
      useState(false);
    const [initialQuestionnaireReference, setInitialQuestionnaireReference] =
      useState<string | undefined>(undefined);
    const [
      initialQuestionnaireResponseAnchorIso,
      setInitialQuestionnaireResponseAnchorIso,
    ] = useState<string | undefined>(undefined);
    const [viewResponsesLoadingTaskKey, setViewResponsesLoadingTaskKey] =
      useState<string | null>(null);
    const [
      questionnaireResponseEnrichedRows,
      setQuestionnaireResponseEnrichedRows,
    ] = useState<EnrichedQuestionnaireResponseRow[]>([]);
    const [questionnaireResponsesLoading, setQuestionnaireResponsesLoading] =
      useState(false);
    /** Evita reentrância sem colocar loading no deps do useCallback (causava loop na aba Respondidos). */
    const questionnaireResponsesInFlightRef = useRef(false);
    const [receivedConsents, setReceivedConsents] = useState<
      ConsentReceivedLite[]
    >([]);
    const [dependentQuestionnaireTasks, setDependentQuestionnaireTasks] =
      useState<fhir5.Task[]>([]);
    const [dialogSummaryBundle, setDialogSummaryBundle] = useState<
      fhir5.Bundle | undefined
    >(undefined);

    const patientSummaryBundleRef = useRef<fhir5.Bundle | undefined>(undefined);
    /** Cache de bundles por Patient reference (titular + dependentes consentidos). */
    const bundleBySubjectPatientRef = useRef<Map<string, fhir5.Bundle>>(
      new Map(),
    );

    const myPatientReference = useMemo(() => {
      const username = user?.username;
      if (typeof username !== "string" || !/^\d{11}$/.test(username)) {
        return "";
      }
      return `Patient/${oids.cpf}-${username}`;
    }, [user?.username]);

    const verifiedDependents = useMemo(() => {
      return (patientRelated ?? []).filter((el: any) => el?.isVerify);
    }, [patientRelated]);

    useEffect(() => {
      if (isTutorMode) {
        setReceivedConsents([]);
        return;
      }
      async function loadConsents() {
        const username = user?.username;
        if (typeof username !== "string" || !username) {
          setReceivedConsents([]);
          return;
        }
        try {
          const receivedResponse = await GetConsents({
            identifier: username,
            type: "received",
          });
          setReceivedConsents(
            receivedResponse?.data
              ?.filter(
                (el: any) => el?.recurso?.id !== username && el?.recurso?.nome,
              )
              ?.map((el: any) => ({
                id: el?.recurso?.id,
                purpose: el?.contexto?.motivoAcesso,
              })) ?? [],
          );
        } catch (err) {
          console.log("PatientQuestionnaireTasksSection GetConsents", err);
          setReceivedConsents([]);
        }
      }
      if (access?.type === "patient") void loadConsents();
    }, [access?.type, isTutorMode, user?.username]);

    const authorizedDependentPatientReferences = useMemo(() => {
      const refs: string[] = [];
      verifiedDependents.forEach((dep: any) => {
        const depId = getDependentIdentifier(dep);
        if (!depId) return;
        if (!isTutorMode) {
          if (!receivedConsents.find((c) => c?.id === depId)?.purpose) return;
        }
        const pref = dependentPatientReferenceFromRecord(dep, oids.cpf);
        if (pref) refs.push(pref);
      });
      return refs;
    }, [isTutorMode, receivedConsents, verifiedDependents]);

    const dependentDisplayByPatientRef = useMemo(() => {
      const m = new Map<string, string>();
      verifiedDependents.forEach((dep: any) => {
        const pref = dependentPatientReferenceFromRecord(dep, oids.cpf);
        const label = String(dep?.name?.[0]?.text ?? "").trim();
        if (pref && label) m.set(pref.trim(), label);
      });
      return m;
    }, [verifiedDependents]);

    useEffect(() => {
      async function loadDependentTasks() {
        if (!authorizedDependentPatientReferences.length) {
          setDependentQuestionnaireTasks([]);
          return;
        }
        try {
          const bundles = await Promise.all(
            authorizedDependentPatientReferences.map((subjectRef) =>
              GetTask({ subject: subjectRef }),
            ),
          );
          const resources = bundles.flatMap(
            (b: any) => b?.entry?.map((e: any) => e.resource) ?? [],
          );
          const dedup = new Map<string, fhir5.Task>();
          resources.forEach((t: fhir5.Task) => {
            if (!t?.id || !isQuestionnaireTask(t)) return;
            dedup.set(String(t.id), t);
          });
          setDependentQuestionnaireTasks(Array.from(dedup.values()));
        } catch (err) {
          console.log(
            "PatientQuestionnaireTasksSection GetTask dependentes",
            err,
          );
          setDependentQuestionnaireTasks([]);
        }
      }
      if (access?.type === "patient" || isTutorMode) void loadDependentTasks();
    }, [access?.type, authorizedDependentPatientReferences, isTutorMode]);

    const questionnaireTasks = useMemo(() => {
      if (isTutorMode) {
        if (!authorizedDependentPatientReferences.length) return [];
        const allowed = new Set(
          authorizedDependentPatientReferences.map((r) => r.trim()),
        );
        const merged = new Map<string, fhir5.Task>();
        [...allTasks, ...dependentQuestionnaireTasks].forEach(
          (task: fhir5.Task) => {
            if (!isQuestionnaireTask(task)) return;
            const forRef = String(task.for?.reference ?? "").trim();
            if (!forRef || !allowed.has(forRef)) return;
            if (task.id) merged.set(String(task.id), task);
          },
        );
        return Array.from(merged.values()).sort(
          (taskA: fhir5.Task, taskB: fhir5.Task) => {
            const dateA = dayjs(taskA.authoredOn || taskA.meta?.lastUpdated);
            const dateB = dayjs(taskB.authoredOn || taskB.meta?.lastUpdated);
            return dateB.valueOf() - dateA.valueOf();
          },
        );
      }
      if (!myPatientReference) return [];
      const allowed = new Set<string>([
        myPatientReference.trim(),
        ...authorizedDependentPatientReferences.map((r) => r.trim()),
      ]);
      const merged = new Map<string, fhir5.Task>();
      [...allTasks, ...dependentQuestionnaireTasks].forEach(
        (task: fhir5.Task) => {
          if (!isQuestionnaireTask(task)) return;
          const forRef = String(task.for?.reference ?? "").trim();
          if (!forRef || !allowed.has(forRef)) return;
          if (task.id) merged.set(String(task.id), task);
        },
      );
      return Array.from(merged.values()).sort(
        (taskA: fhir5.Task, taskB: fhir5.Task) => {
          const dateA = dayjs(taskA.authoredOn || taskA.meta?.lastUpdated);
          const dateB = dayjs(taskB.authoredOn || taskB.meta?.lastUpdated);
          return dateB.valueOf() - dateA.valueOf();
        },
      );
    }, [
      allTasks,
      authorizedDependentPatientReferences,
      dependentQuestionnaireTasks,
      isTutorMode,
      myPatientReference,
    ]);

    const ensurePatientSummaryBundle = useCallback(async () => {
      if (patientSummaryBundleRef.current) {
        bundleBySubjectPatientRef.current.set(
          myPatientReference.trim(),
          patientSummaryBundleRef.current,
        );
        return patientSummaryBundleRef.current;
      }
      const username = user?.username;
      if (typeof username !== "string" || !username) {
        toast.error("Não foi possível carregar seus questionários.");
        return undefined;
      }
      // MHD: mesmo recorte que `PatientSummary` + `BundlePatientSummary` com `entries`
      // (só QuestionnaireResponse — ver `AuxiliarData.PatientSummary` / filterEntries).
      const bundleResponse = await BundlePatientSummary({
        identifier: username,
        purposeofuse: "Atendimento",
        entries: PATIENT_SUMMARY_ENTRIES_QUESTIONNAIRE_RESPONSE_ONLY,
      });
      if (!bundleResponse) {
        return undefined;
      }
      if (
        typeof bundleResponse === "object" &&
        "status" in bundleResponse &&
        bundleResponse.status === HTTPResponseStatus.Forbidden
      ) {
        navigate("/");
        return undefined;
      }
      const bundle = bundleResponse as fhir5.Bundle;
      patientSummaryBundleRef.current = bundle;
      bundleBySubjectPatientRef.current.set(myPatientReference.trim(), bundle);
      return bundle;
    }, [myPatientReference, navigate, user?.username]);

    /**
     * Resolve o bundle Patient Summary para abrir o diálogo de respostas
     * (titular ou dependente), buscando sob demanda se ainda não estiver em cache.
     */
    const resolveBundleForSubjectPatientReference = useCallback(
      async (subjectPatientRef: string): Promise<fhir5.Bundle | undefined> => {
        const key = subjectPatientRef.trim();
        const cached = bundleBySubjectPatientRef.current.get(key);
        if (cached) return cached;
        if (key === myPatientReference.trim()) {
          return ensurePatientSummaryBundle();
        }
        const dep = verifiedDependents.find(
          (d: any) =>
            dependentPatientReferenceFromRecord(d, oids.cpf)?.trim() === key,
        );
        /** Consent “received” no legado usa o id derivado do dependente (ex. CPF). */
        const dependenteIdForConsent = dep
          ? getDependentIdentifier(dep)
          : undefined;
        if (isTutorMode) {
          const dependenteIdForSummary =
            dep != null
              ? patientSummaryIdentifierFromRelatedDependent(dep)
              : undefined;
          if (!dependenteIdForSummary) {
            toast.error("Não foi possível carregar o prontuário deste pet.");
            return undefined;
          }
          const bundleResponse = await BundlePatientSummary({
            identifier: dependenteIdForSummary,
            purposeofuse: "Atendimento",
            entries: PATIENT_SUMMARY_ENTRIES_QUESTIONNAIRE_RESPONSE_ONLY,
          });
          if (
            typeof bundleResponse === "object" &&
            bundleResponse &&
            "status" in bundleResponse &&
            bundleResponse.status === HTTPResponseStatus.Forbidden
          ) {
            navigate("/");
            return undefined;
          }
          const bundle = bundleResponse as fhir5.Bundle;
          bundleBySubjectPatientRef.current.set(key, bundle);
          return bundle;
        }
        const purpose = dependenteIdForConsent
          ? receivedConsents.find((c) => c?.id === dependenteIdForConsent)
              ?.purpose
          : undefined;
        if (!dependenteIdForConsent || !purpose) {
          toast.error(
            "Não foi possível carregar o prontuário deste dependente.",
          );
          return undefined;
        }
        const bundleResponse = await BundlePatientSummary({
          identifier: dependenteIdForConsent,
          purposeofuse: purpose,
          entries: PATIENT_SUMMARY_ENTRIES_QUESTIONNAIRE_RESPONSE_ONLY,
        });
        if (
          typeof bundleResponse === "object" &&
          bundleResponse &&
          "status" in bundleResponse &&
          bundleResponse.status === HTTPResponseStatus.Forbidden
        ) {
          navigate("/");
          return undefined;
        }
        const bundle = bundleResponse as fhir5.Bundle;
        bundleBySubjectPatientRef.current.set(key, bundle);
        return bundle;
      },
      [
        ensurePatientSummaryBundle,
        isTutorMode,
        myPatientReference,
        navigate,
        receivedConsents,
        verifiedDependents,
      ],
    );

    const ensureQuestionnaireResponsesWithUsageTypeExtension =
      useCallback(async () => {
        if (questionnaireResponsesInFlightRef.current) return;
        questionnaireResponsesInFlightRef.current = true;

        setQuestionnaireResponsesLoading(true);
        try {
          if (isTutorMode) {
            const dependentRows =
              verifiedDependents.length > 0
                ? await fetchVerifiedPetQuestionnaireSummaryEntries(
                    verifiedDependents,
                  )
                : [];

            dependentRows.forEach((row) => {
              bundleBySubjectPatientRef.current.set(
                row.subjectPatientReference.trim(),
                row.bundle,
              );
            });

            const mergedDependentEntries: EnrichedQuestionnaireResponseRow[] =
              dependentRows.flatMap((row) =>
                row.filteredEntries.map((bundleEntry: any) => ({
                  bundleEntry,
                  subjectPatientReference: row.subjectPatientReference.trim(),
                  subjectPatientDisplayName: row.subjectPatientDisplayName,
                })),
              );

            const dedupVet = new Map<
              string,
              EnrichedQuestionnaireResponseRow
            >();
            mergedDependentEntries.forEach((row) => {
              const resource = row.bundleEntry?.resource;
              const id =
                resource?.id != null && String(resource.id).trim()
                  ? String(resource.id).trim()
                  : undefined;
              const dedupKey = id
                ? `${row.subjectPatientReference}|${id}`
                : `${row.subjectPatientReference}|${JSON.stringify(row.bundleEntry)}`;
              dedupVet.set(dedupKey, row);
            });

            setQuestionnaireResponseEnrichedRows(Array.from(dedupVet.values()));
            return;
          }

          const bundle = await ensurePatientSummaryBundle();
          if (!bundle) {
            setQuestionnaireResponseEnrichedRows([]);
            return;
          }

          const titularFiltered =
            filterQuestionnaireResponseEntriesWithUsageType(bundle);
          const titularRows: EnrichedQuestionnaireResponseRow[] =
            titularFiltered.map((bundleEntry: any) => ({
              bundleEntry,
              subjectPatientReference: myPatientReference.trim(),
              subjectPatientDisplayName: undefined,
            }));

          const dependentRows =
            verifiedDependents.length && receivedConsents.length
              ? await fetchDependentPatientSummaryQuestionnaireEntries({
                  dependents: verifiedDependents,
                  receivedConsents,
                })
              : [];

          dependentRows.forEach((row) => {
            bundleBySubjectPatientRef.current.set(
              row.subjectPatientReference.trim(),
              row.bundle,
            );
          });

          const mergedDependentEntries: EnrichedQuestionnaireResponseRow[] =
            dependentRows.flatMap((row) =>
              row.filteredEntries.map((bundleEntry: any) => ({
                bundleEntry,
                subjectPatientReference: row.subjectPatientReference.trim(),
                subjectPatientDisplayName: row.subjectPatientDisplayName,
              })),
            );

          const dedup = new Map<string, EnrichedQuestionnaireResponseRow>();
          [...titularRows, ...mergedDependentEntries].forEach((row) => {
            const resource = row.bundleEntry?.resource;
            const id =
              resource?.id != null && String(resource.id).trim()
                ? String(resource.id).trim()
                : undefined;
            const dedupKey = id
              ? `${row.subjectPatientReference}|${id}`
              : `${row.subjectPatientReference}|${JSON.stringify(row.bundleEntry)}`;
            dedup.set(dedupKey, row);
          });

          setQuestionnaireResponseEnrichedRows(Array.from(dedup.values()));
        } catch (err) {
          console.log("PatientQuestionnaireTasksSection error:", err);
          setQuestionnaireResponseEnrichedRows([]);
        } finally {
          questionnaireResponsesInFlightRef.current = false;
          setQuestionnaireResponsesLoading(false);
        }
      }, [
        ensurePatientSummaryBundle,
        isTutorMode,
        myPatientReference,
        receivedConsents,
        verifiedDependents,
      ]);

    useEffect(() => {
      if (tasksTab === "completed" || tasksTab === "all") {
        void ensureQuestionnaireResponsesWithUsageTypeExtension();
      }
    }, [ensureQuestionnaireResponsesWithUsageTypeExtension, tasksTab]);

    useEffect(() => {
      patientSummaryBundleRef.current = undefined;
      bundleBySubjectPatientRef.current.clear();
      setQuestionnaireResponseEnrichedRows([]);
      setDependentQuestionnaireTasks([]);
      questionnaireResponsesInFlightRef.current = false;
    }, [user?.username]);

    const responseItems = useMemo((): QuestionnaireListItem[] => {
      return (questionnaireResponseEnrichedRows ?? [])
        .map((row: EnrichedQuestionnaireResponseRow, index: number) => {
          const questionnaireResponse = row.bundleEntry?.resource as
            | fhir5.QuestionnaireResponse
            | undefined;
          if (!questionnaireResponse) return null;
          const questionnaireReference = questionnaireReferenceFromResponse(
            questionnaireResponse,
          );
          const dateIso = dateIsoFromResponse(questionnaireResponse);
          const title = questionnaireTitleFromResponse(questionnaireResponse);

          return {
            kind: "response" as const,
            key:
              String(questionnaireResponse.id ?? "").trim() ||
              `${row.subjectPatientReference}|${questionnaireReference ?? index}`,
            title,
            dateIso,
            dateLabel: dateLabelFromIso(dateIso),
            questionnaireReference,
            questionnaireResponse,
            subjectPatientReference: row.subjectPatientReference,
            subjectPatientDisplayName: row.subjectPatientDisplayName,
          };
        })
        .filter(Boolean) as QuestionnaireListItem[];
    }, [questionnaireResponseEnrichedRows]);

    const taskItems = useMemo((): Extract<
      QuestionnaireListItem,
      { kind: "task" }
    >[] => {
      return questionnaireTasks.map((task: fhir5.Task, index) => {
        const questionnaireReference = questionnaireReferenceFromTask(task);
        const dateIsoRaw = String(
          task.authoredOn || task.meta?.lastUpdated || "",
        ).trim();
        const dateIso = dateIsoRaw || undefined;
        const forRef = String(task.for?.reference ?? "").trim();
        const isDependentTask =
          forRef.length > 0 && forRef !== myPatientReference.trim();
        return {
          kind: "task",
          key: task.id?.trim() ?? `patient-q-task-row-${index}`,
          title: String(task.description || "Questionário").trim(),
          dateIso,
          dateLabel: dateLabelFromIso(dateIso),
          questionnaireReference,
          task,
          subjectPatientReference: forRef || myPatientReference.trim(),
          subjectPatientDisplayName: isDependentTask
            ? dependentDisplayByPatientRef.get(forRef)
            : undefined,
        };
      });
    }, [dependentDisplayByPatientRef, myPatientReference, questionnaireTasks]);

    const openTaskItems = useMemo(() => {
      return taskItems.filter((it) => it.task.status === "requested");
    }, [taskItems]);

    const allTabItems = useMemo((): QuestionnaireListItem[] => {
      /**
       * Regra de negócio (dashboard):
       * - "Abertos": apenas Tasks requested
       * - "Respondidos": apenas QuestionnaireResponses (com usage-type extension)
       * - "Todos": Abertos (Tasks) + Respondidos (QuestionnaireResponses)
       */
      const merged: QuestionnaireListItem[] = [
        ...openTaskItems,
        ...responseItems,
      ];

      return merged.sort((a, b) => {
        const da = dayjs(a.dateIso || undefined);
        const db = dayjs(b.dateIso || undefined);
        if (da.isValid() && db.isValid()) return db.valueOf() - da.valueOf();
        if (db.isValid()) return 1;
        if (da.isValid()) return -1;
        return 0;
      });
    }, [openTaskItems, responseItems]);

    const filteredItems = useMemo((): QuestionnaireListItem[] => {
      if (tasksTab === "open") {
        return openTaskItems;
      }
      if (tasksTab === "completed") {
        return responseItems;
      }
      return allTabItems;
    }, [allTabItems, openTaskItems, responseItems, tasksTab]);

    const paginatedItems = useMemo(() => {
      const startIndex = page * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      return filteredItems.slice(startIndex, endIndex);
    }, [filteredItems, page, rowsPerPage]);

    const handleChangeTasksTab = useCallback(
      (_e: React.SyntheticEvent, next: TasksTabValue) => {
        setTasksTab(next);
        setPage(0);
      },
      [],
    );

    const handleChangePage = useCallback((_: unknown, newPage: number) => {
      setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
      },
      [],
    );

    const handleOpenQuestionnaire = useCallback(
      (task: fhir5.Task) => {
        const focusReference = task.focus?.reference?.trim() ?? "";
        const questionnaireId = focusReference.startsWith("Questionnaire/")
          ? focusReference.replace("Questionnaire/", "").trim()
          : "";

        const patientTaskId = task.id?.trim() ?? "";
        const subjectFromTask = String(task.for?.reference ?? "").trim();
        const subjectReference = subjectFromTask || myPatientReference.trim();
        const requesterPractitionerRoleReference =
          task.requester?.reference?.trim() ?? "";
        const ownerOrganizationReference = task.owner?.reference?.trim() ?? "";
        const questionnaireTitle = (task.description || "Questionário").trim();
        const appointmentReference =
          getAppointmentReferenceFromQuestionnaireTask(task);

        const isAnsweringForDependent =
          subjectReference.trim() !== myPatientReference.trim();

        if (
          !questionnaireId ||
          !patientTaskId ||
          !subjectReference ||
          !requesterPractitionerRoleReference ||
          !ownerOrganizationReference
        ) {
          toast.error(
            "Não foi possível abrir o questionário (dados da task incompletos).",
          );
          return;
        }

        setIsSidebarExpanded(false);

        navigate("/questionnaire-respond", {
          state: {
            questionnaireId,
            questionnaireTitle,
            subjectReference,
            patientTaskId,
            requesterPractitionerRoleReference,
            ownerOrganizationReference,
            ...(appointmentReference ? { appointmentReference } : {}),
            ...(isAnsweringForDependent
              ? {
                  authorReference: myPatientReference.trim(),
                  subjectPatientDisplayName:
                    dependentDisplayByPatientRef.get(subjectReference.trim()) ||
                    undefined,
                }
              : {}),
          },
        });
      },
      [
        dependentDisplayByPatientRef,
        myPatientReference,
        navigate,
        setIsSidebarExpanded,
      ],
    );

    const handleViewResponses = useCallback(
      async (task: fhir5.Task, taskRowKey: string) => {
        const focusRef = task.focus?.reference?.trim() || undefined;
        const subjectRef =
          String(task.for?.reference ?? "").trim() || myPatientReference;
        setViewResponsesLoadingTaskKey(taskRowKey);
        try {
          const bundle =
            await resolveBundleForSubjectPatientReference(subjectRef);
          if (!bundle) return;
          setDialogSummaryBundle(bundle);
          const metaStr =
            task.meta?.lastUpdated != null
              ? String(task.meta.lastUpdated).trim()
              : "";
          const authoredStr =
            task.authoredOn != null ? String(task.authoredOn).trim() : "";
          const anchorIso = metaStr || authoredStr || undefined;
          setInitialQuestionnaireResponseAnchorIso(anchorIso);
          setInitialQuestionnaireReference(focusRef);
          setQuestionnairesDialogOpen(true);
        } catch (err) {
          console.log("PatientSummary error:", err);
          toast.error("Não foi possível carregar suas respostas agora.");
        } finally {
          setViewResponsesLoadingTaskKey(null);
        }
      },
      [myPatientReference, resolveBundleForSubjectPatientReference],
    );

    const handleViewResponsesFromResponse = useCallback(
      async (item: Extract<QuestionnaireListItem, { kind: "response" }>) => {
        setViewResponsesLoadingTaskKey(item.key);
        try {
          const bundle = await resolveBundleForSubjectPatientReference(
            item.subjectPatientReference,
          );
          if (!bundle) return;
          setDialogSummaryBundle(bundle);

          const anchorIso =
            dateIsoFromResponse(item.questionnaireResponse) || undefined;
          setInitialQuestionnaireResponseAnchorIso(anchorIso);

          const questionnaireRef =
            item.questionnaireReference ||
            questionnaireReferenceFromResponse(item.questionnaireResponse);
          setInitialQuestionnaireReference(questionnaireRef);
          setQuestionnairesDialogOpen(true);
        } catch (err) {
          console.log("PatientSummary error:", err);
          toast.error("Não foi possível carregar suas respostas agora.");
        } finally {
          setViewResponsesLoadingTaskKey(null);
        }
      },
      [resolveBundleForSubjectPatientReference],
    );

    const handleCloseQuestionnairesDialog = useCallback(() => {
      setQuestionnairesDialogOpen(false);
      setInitialQuestionnaireReference(undefined);
      setInitialQuestionnaireResponseAnchorIso(undefined);
      setDialogSummaryBundle(undefined);
    }, []);

    const tabSx = useMemo(
      () => ({
        minHeight: TOUCH_MIN_PX,
        textTransform: "none" as const,
        px: 1.5,
      }),
      [],
    );

    const showQuestionnaireSection = access?.type === "patient" || isTutorMode;

    if (!showQuestionnaireSection) {
      return null;
    }

    if (!myPatientReference) {
      return null;
    }

    const emptyMessage =
      tasksTab === "open"
        ? "Nenhum questionário aberto."
        : tasksTab === "completed"
          ? "Nenhum questionário respondido."
          : "Nenhum questionário enviado pela equipe.";

    return (
      <>
        <Card
          elevation={0}
          sx={{
            borderRadius: { xs: "8px", md: "16px" },
            boxShadow:
              "rgba(0, 0, 0, 0.2) 0px 3px 1px -2px, rgba(0, 0, 0, 0.14) 0px 2px 2px 0px, rgba(0, 0, 0, 0.12) 0px 1px 5px 0px",
            bgcolor: "neutral0.main",
          }}
        >
          <CardContent sx={{ py: 2, px: { xs: 2, md: 3 } }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
              Meus questionários
            </Typography>

            <Tabs
              value={tasksTab}
              onChange={handleChangeTasksTab}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: TOUCH_MIN_PX,
                mb: 2,
                "& .MuiTabScrollButton-root": {
                  minWidth: TOUCH_MIN_PX,
                },
              }}
            >
              {TASKS_TABS.map((t) => (
                <Tab key={t.value} label={t.label} value={t.value} sx={tabSx} />
              ))}
            </Tabs>

            {filteredItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {emptyMessage}
              </Typography>
            ) : (
              <Stack spacing={2}>
                {paginatedItems.map((item: QuestionnaireListItem, index) => {
                  const isTask = item.kind === "task";
                  const task = isTask ? item.task : item.task;
                  const isPending =
                    isTask && (task?.status === "requested" || !task?.status);
                  const isDone = isTask && task?.status === "completed";
                  const isResponse = item.kind === "response";

                  const authoredLabel =
                    item.dateLabel ||
                    (isTask && task?.authoredOn
                      ? dayjs(task.authoredOn).format("DD/MM/YYYY HH:mm")
                      : "");

                  const showOpenButton =
                    isTask &&
                    (tasksTab === "open" || (tasksTab === "all" && isPending));
                  const showViewButton =
                    isResponse ||
                    (isTask &&
                      (tasksTab === "completed" ||
                        (tasksTab === "all" && isDone)));

                  const rowKey = item.key || `patient-q-row-${index}`;
                  const isThisViewLoading =
                    viewResponsesLoadingTaskKey === rowKey;

                  const isDependentRow =
                    item.subjectPatientReference.trim() !==
                    myPatientReference.trim();

                  const dependentChipLabel = item.subjectPatientDisplayName
                    ? `${isTutorMode ? "Pet" : "Dependente"}: ${item.subjectPatientDisplayName}`
                    : isTutorMode
                      ? "Pet"
                      : "Dependente";

                  const showProxyAnsweredChip =
                    isResponse &&
                    isRespondedByResponsibleForViewer(
                      item.questionnaireResponse,
                      myPatientReference.trim(),
                    );

                  return (
                    <Box
                      key={rowKey}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        alignItems={{ xs: "stretch", sm: "center" }}
                        justifyContent="space-between"
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {item.title || "Questionário"}
                          </Typography>
                          {authoredLabel ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Enviado em {authoredLabel}
                            </Typography>
                          ) : null}
                          <Stack
                            direction="row"
                            spacing={0.75}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mt: 0.5 }}
                            aria-label="Indicadores do questionário"
                          >
                            <Chip
                              size="small"
                              label={
                                isResponse
                                  ? "Respondido"
                                  : isPending
                                    ? "Pendente"
                                    : "Respondido"
                              }
                              color={
                                isResponse
                                  ? "success"
                                  : isPending
                                    ? "warning"
                                    : "success"
                              }
                              variant="outlined"
                            />
                            {isDependentRow ? (
                              <Chip
                                size="small"
                                color="info"
                                variant="outlined"
                                label={dependentChipLabel}
                              />
                            ) : null}
                            {showProxyAnsweredChip ? (
                              <Chip
                                size="small"
                                color="secondary"
                                variant="outlined"
                                label="Respondido por responsável"
                              />
                            ) : null}
                          </Stack>
                        </Box>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          sx={{ flexShrink: 0 }}
                        >
                          {showOpenButton ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<OpenInNew />}
                              onClick={() => {
                                if (isTask) {
                                  handleOpenQuestionnaire(item.task);
                                }
                              }}
                              disabled={!isPending || !item.task?.id}
                              sx={{ minHeight: TOUCH_MIN_PX }}
                            >
                              Abrir questionário
                            </Button>
                          ) : null}
                          {showViewButton ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={
                                isThisViewLoading ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <Visibility />
                                )
                              }
                              onClick={() => {
                                if (item.kind === "response") {
                                  void handleViewResponsesFromResponse(item);
                                  return;
                                }
                                void handleViewResponses(item.task, rowKey);
                              }}
                              disabled={
                                viewResponsesLoadingTaskKey !== null ||
                                questionnaireResponsesLoading
                              }
                              sx={{ minHeight: TOUCH_MIN_PX }}
                            >
                              Ver minhas respostas
                            </Button>
                          ) : null}
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
                <TablePagination
                  sx={tablePaginationStyles}
                  labelRowsPerPage="Exibir"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count}`
                  }
                  rowsPerPageOptions={[1, 3, 5, 10, 20, 50, 100]}
                  component="div"
                  count={filteredItems.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Stack>
            )}
          </CardContent>
        </Card>

        {questionnairesDialogOpen ? (
          <PatientQuestionnairesDialog
            open={questionnairesDialogOpen}
            onClose={handleCloseQuestionnairesDialog}
            summaryData={dialogSummaryBundle ?? patientSummaryBundleRef.current}
            initialQuestionnaireReference={initialQuestionnaireReference}
            initialQuestionnaireResponseAnchorIso={
              initialQuestionnaireResponseAnchorIso
            }
          />
        ) : null}
      </>
    );
  },
);
